import { DateTime } from 'luxon';

import DemographicType from '@/common/enums/DemographicType.enum';
import { AudienceFile } from '@/common/models';
import { IAudienceFileAPI } from '@/common/models/audienceFiles/AudienceFile';
import { DASH } from '@/globals';
import Validatable from '@/validator/Validatable';

interface IDemographicAPI {
  demographic_id: number;
  name: string;
  type: string;
  sub_type: string;
  code: string;
  show_buyers: boolean;
  created_on_utc?: string;
  modified_on_utc?: string;
  audiencefile?: IAudienceFileAPI;
  dataprovider_id?: number;
  organization_id?: number;
}

export default class Demographic extends Validatable {
  name: string;
  type: string;
  subType: string;
  code: string;
  showBuyers: boolean;
  createdOnUtc?: DateTime;
  modifiedOnUtc?: DateTime;
  audienceFile?: AudienceFile;
  dataProviderId?: number;
  organizationId?: number;

  constructor(
    id: number = -1,
    name: string = '',
    type: string = null,
    subType: string = null,
    code: string = null,
    showBuyers: boolean = false,
    createdOnUtc: DateTime = null,
    modifiedOnUtc: DateTime = null,
    audienceFile: AudienceFile = null,
    dataProviderId: number = null,
    organizationId: number = null,
  ) {
    super();
    this.id = id;
    this.name = name;
    this.type = type;
    this.subType = subType;
    this.code = code;
    this.showBuyers = showBuyers;
    this.createdOnUtc = createdOnUtc;
    this.modifiedOnUtc = modifiedOnUtc;
    this.audienceFile = audienceFile;
    this.dataProviderId = dataProviderId;
    this.organizationId = organizationId;
  }

  isCustom(): boolean {
    return this.type === DemographicType.Custom;
  }

  isAgeGender(): boolean {
    if (this.type === DemographicType.Predefined) {
      return this.subType === DemographicType.Gender || this.subType === DemographicType.AgeGender;
    }
    return this.type === DemographicType.Age || this.type === DemographicType.Gender;
  }

  get modifiedOnFormatted(): string {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get createdOnFormatted(): string {
    return this.createdOnUtc != null ? this.createdOnUtc.toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  static fromObject(obj: object): Demographic {
    return super.fromObject(obj, new Demographic());
  }

  static fromApi(apiObject: IDemographicAPI): Demographic {
    return new Demographic(
      apiObject.demographic_id,
      apiObject.name,
      apiObject.type,
      apiObject.sub_type,
      apiObject.code,
      apiObject.show_buyers,
      apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      apiObject.audiencefile != null ? AudienceFile.fromApi(apiObject.audiencefile) : null,
      apiObject.dataprovider_id,
      apiObject.organization_id,
    );
  }

  toApi(): IDemographicAPI {
    return {
      demographic_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      type: this.type,
      sub_type: this.subType,
      code: this.code,
      show_buyers: this.showBuyers,
    };
  }

  static validatorCreateRules(): Record<string, string> {
    return {
      name: 'required|max:100|not_empty_or_whitespace',
    };
  }
}
